/**
 * ReportConfigPanel · Sprint P37c · Feb 2026
 *
 * Compact, expandable per-project report configuration:
 *   • Report Type + Primary Standard (JORC / NI 43-101 / SEC / UNFC / Internal)
 *   • Report Status (DRAFT / REVIEW / FINAL)
 *   • Cover — title / subtitle / status-label overrides
 *   • Cover Hero Image — Off (default) | 3D Model | Custom upload
 *   • Consultant identity — name / short / address
 *   • Logo — upload / preview / remove; position (left/right/none)
 *   • Header + Footer text overrides (left/center/right)
 *   • Watermark toggle + custom text
 *   • Also acts as the settings surface for the LLM-chapter toggle
 *
 * The panel writes to `/api/projects/{pid}/report-branding` (merge-safe).
 * The consumer reads the current selection via ref props so the Download
 * button can pass report_type + primary_standard + status as query params.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  CaretDown, CaretRight, Image as ImageIcon, Trash, Upload,
  CheckCircle, Sliders, Sparkle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";


// ── Enums ────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { v: "CPR",              l: "Competent Person's Report" },
  { v: "EXTENDED",         l: "★ Extended CPR (Premium · KPI dashboard, variography, section atlas)" },
  { v: "MRE",              l: "Mineral Resource Estimate" },
  { v: "EXPLORATION",      l: "Exploration Report" },
  { v: "SCOPING",          l: "Scoping Study" },
  { v: "PEA",              l: "Preliminary Economic Assessment" },
  { v: "PFS",              l: "Pre-Feasibility Study" },
  { v: "DFS",              l: "Definitive Feasibility Study" },
  { v: "BFS",              l: "Bankable Feasibility Study" },
  { v: "RESOURCE_RESERVE", l: "Resource & Reserve Report" },
  { v: "DUE_DILIGENCE",    l: "Technical Due Diligence" },
  { v: "INTERNAL",         l: "Internal Technical Report" },
];

const STANDARDS = [
  { v: "JORC_2012",   l: "JORC 2012 (CP · Ore Reserve)" },
  { v: "NI_43_101",   l: "NI 43-101 / CIM (QP · Mineral Reserve)" },
  { v: "SEC_SK_1300", l: "SEC S-K 1300 (Technical Report Summary)" },
  { v: "UNFC",        l: "UNFC (E / F / G axes)" },
  { v: "SAMREC",      l: "SAMREC 2016" },
  { v: "PERC",        l: "PERC (Europe)" },
  { v: "INTERNAL",    l: "Internal — Not for Public Reporting" },
];

const STATUSES = [
  { v: "DRAFT",  l: "Draft" },
  { v: "REVIEW", l: "Under Review" },
  { v: "FINAL",  l: "Final" },
];

const HERO_MODES = [
  { v: "off",           l: "None (recommended)" },
  { v: "3d_model",      l: "3D Model Snapshot" },
  { v: "custom_upload", l: "Custom Upload" },
];

// ── AI Report Templates ─────────────────────────────────────────────
// Feb 2026 · Iter87 · one-click template picker so first-time authors
// get a valid draft configuration without knowing which standard
// applies to which report type. Each template writes to
// report_type + primary_standard + cover_title_override +
// cover_subtitle_override + llm_chapters and stops there — the user
// can still tweak anything after applying.
const REPORT_TEMPLATES = [
  {
    key: "cpr_jorc",
    icon: "📜",
    name: "Competent Person's Report",
    tagline: "JORC 2012 · public-reporting CPR for ASX/AIM",
    values: {
      report_type: "CPR", primary_standard: "JORC_2012",
      cover_title_override: "COMPETENT PERSON'S REPORT",
      cover_subtitle_override: "JORC 2012 · Mineral Resources & Ore Reserves",
      llm_chapters: true,
    },
    chapters: ["Executive Summary", "Geology & Deposit", "Drilling & Sampling",
               "QA/QC", "Mineral Resource Estimation", "Classification",
               "Reasonable Prospects", "Ore Reserves", "Competent Person Statement"],
  },
  {
    key: "mre",
    icon: "📈",
    name: "Mineral Resource Estimate",
    tagline: "JORC 2012 or NI 43-101 · resource-only report",
    values: {
      report_type: "MRE", primary_standard: "JORC_2012",
      cover_title_override: "MINERAL RESOURCE ESTIMATE",
      cover_subtitle_override: "Resource Model · Classification · Reconciliation",
      llm_chapters: true,
    },
    chapters: ["Deposit Setting", "Drillhole Database", "QA/QC Summary",
               "Domain Modelling", "Grade Estimation", "Model Validation",
               "Classification (Measured/Indicated/Inferred)", "Statement of MRE"],
  },
  {
    key: "exploration",
    icon: "🔎",
    name: "Exploration Report",
    tagline: "JORC Table 1 · early-stage results & pipeline",
    values: {
      report_type: "EXPLORATION", primary_standard: "JORC_2012",
      cover_title_override: "EXPLORATION REPORT",
      cover_subtitle_override: "Regional Setting · Targets · Programme Results",
      llm_chapters: true,
    },
    chapters: ["Regional Geology", "Prospect Overview", "Exploration Programme",
               "Sampling & Assays", "Anomalies & Targets", "Next Steps"],
  },
  {
    key: "pfs",
    icon: "🏗️",
    name: "Pre-Feasibility Study (PFS)",
    tagline: "NI 43-101 / JORC · engineering economics",
    values: {
      report_type: "PFS", primary_standard: "NI_43_101",
      cover_title_override: "PRE-FEASIBILITY STUDY",
      cover_subtitle_override: "Technical & Economic Assessment",
      llm_chapters: true,
    },
    chapters: ["Resource Statement", "Reserve Estimate", "Mining Method",
               "Processing", "Infrastructure", "Capex & Opex",
               "Economic Analysis", "Risk & Sensitivity"],
  },
  {
    key: "dfs",
    icon: "💠",
    name: "Definitive Feasibility Study (DFS)",
    tagline: "NI 43-101 / SEC S-K 1300 · bankable-grade",
    values: {
      report_type: "DFS", primary_standard: "NI_43_101",
      cover_title_override: "DEFINITIVE FEASIBILITY STUDY",
      cover_subtitle_override: "Bankable Technical & Financial Assessment",
      llm_chapters: true,
    },
    chapters: ["Executive Summary", "Reserves & Mine Design",
               "Processing Design", "Detailed Capex", "Detailed Opex",
               "NPV / IRR / Payback", "Sensitivity & Risk",
               "Development Schedule", "ESG / Permitting"],
  },
  {
    key: "technical",
    icon: "🧪",
    name: "Technical Report",
    tagline: "Internal · methodology & audit trail",
    values: {
      report_type: "INTERNAL", primary_standard: "INTERNAL",
      cover_title_override: "TECHNICAL REPORT",
      cover_subtitle_override: "Methodology · Assumptions · Sensitivity",
      llm_chapters: false,
    },
    chapters: ["Objective", "Data Sources", "Methodology",
               "Estimation Parameters", "Sensitivity Analysis",
               "Assumptions & Limitations"],
  },
];

const LOGO_POSITIONS = [
  { v: "top_right", l: "Top right" },
  { v: "top_left",  l: "Top left" },
  { v: "none",      l: "Hide logo" },
];


// ── Auto-suggest ───────────────────────────────────────────────────
// Feb 2026 · Iter88 · Zero-click report template pick. Maps the project's
// (project_stage, commodity) → the most probable template. Returns the
// TEMPLATE object plus a human-readable reason for the recommendation.
function suggestTemplate({ project_stage, commodity }) {
  // Order matters — the earlier a rule matches, the more confident it is.
  const stage = (project_stage || "").toLowerCase();
  if (stage === "production" || stage === "operating") {
    return { key: "cpr_jorc", reason: "Operating asset → public-reporting CPR" };
  }
  if (stage === "feasibility" || stage === "dfs" || stage === "bankable") {
    return { key: "dfs", reason: "Feasibility-stage asset → DFS bankable study" };
  }
  if (stage === "pre_feasibility" || stage === "pfs") {
    return { key: "pfs", reason: "Pre-feasibility stage → PFS engineering & economics" };
  }
  if (stage === "resource_definition" || stage === "resource_definition_drilling"
      || stage === "advanced_exploration") {
    return { key: "mre", reason: "Resource-definition stage → Mineral Resource Estimate" };
  }
  if (stage === "early_exploration" || stage === "scoping"
      || stage === "scout" || stage === "reconnaissance") {
    return { key: "exploration", reason: "Early-exploration stage → Exploration Report" };
  }
  // Fallback — infer from commodity when project_stage is missing.
  if (commodity) {
    return { key: "mre", reason: `${commodity} project · generic MRE default` };
  }
  return { key: "cpr_jorc", reason: "Generic default · JORC CPR" };
}

export default function ReportConfigPanel({ projectId, onChange }) {
  const [open, setOpen]         = useState(false);
  const [loaded, setLoaded]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  // Iter88 · Feb 2026 · Fetch project + configuration to power the
  // auto-suggest template banner.
  const projectQ = useQuery({
    queryKey: ["project", projectId],
    queryFn:  () => api.get(`/projects/${projectId}`).then((r) => r.data),
    enabled:  !!projectId,
    staleTime: 60_000,
  });
  const cfgQ = useQuery({
    queryKey: ["project-cfg-lite", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/configuration`).then((r) => r.data?.configuration || {}),
    enabled:  !!projectId,
    staleTime: 60_000,
  });
  const [state, setState]       = useState({
    // Selection (not persisted server-side under `report_branding` — passed
    // as query params on the download URL)
    report_type:      "CPR",
    primary_standard: "JORC_2012",
    report_status:    "DRAFT",
    llm_chapters:     false,
    // Branding (persisted server-side)
    cover_title_override:      "",
    cover_subtitle_override:   "",
    cover_status_label:        "",
    cover_hero_image:          "off",
    consultant_name:           "",
    consultant_short:          "",
    consultant_address:        "",
    logo_position:             "top_right",
    client_logo_position:      "cover_client_block",
    client_logo_uploaded:      false,
    confidentiality_notice:    "",
    header_left_override:      "",
    header_right_override:     "",
    header_show_rule:          true,
    footer_left_override:      "",
    footer_center_override:    "",
    footer_right_override:     "",
    footer_show_rule:          true,
    footer_show_page_numbers:  true,
    watermark_enabled:         true,
    watermark_text_override:   "",
    logo_uploaded:             false,
  });

  // ── Load existing branding on mount ────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/report-branding`);
        if (!alive) return;
        setState((s) => ({ ...s, ...r.data }));
      } catch (e) {
        console.warn("Could not load branding:", e?.message);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  // ── Publish selection upward so the Download button can consume it ─
  useEffect(() => {
    onChange?.({
      report_type:      state.report_type,
      primary_standard: state.primary_standard,
      report_status:    state.report_status,
      llm_chapters:     state.llm_chapters,
    });
  }, [state.report_type, state.primary_standard, state.report_status,
       state.llm_chapters, onChange]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  // ── Persist branding fields to server (debounced) ──────────────────
  const debounceRef = useRef(null);
  const persistBranding = useCallback((patch) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/projects/${projectId}/report-branding`, patch);
      } catch (e) {
        toast.error(`Could not save branding: ${e?.message || "network"}`);
      } finally {
        setSaving(false);
      }
    }, 400);
  }, [projectId]);

  const setBranding = (k, v) => {
    setState((s) => ({ ...s, [k]: v }));
    persistBranding({ [k]: v });
  };

  // Iter87 · Feb 2026 · one-click template. Sets the report-type-related
  // fields on `state`, then persists the branding-related keys server-side
  // so the choice sticks across reloads.
  const applyTemplate = useCallback((tpl) => {
    if (!tpl) return;
    setState((s) => ({ ...s, ...tpl.values }));
    const brandingPatch = {};
    for (const k of ["cover_title_override", "cover_subtitle_override"]) {
      if (k in tpl.values) brandingPatch[k] = tpl.values[k];
    }
    if (Object.keys(brandingPatch).length) persistBranding(brandingPatch);
    toast.success(`Applied "${tpl.name}" template`);
  }, [persistBranding]);

  // ── Logo upload / remove ───────────────────────────────────────────
  const fileRef = useRef(null);
  const clientLogoRef = useRef(null);
  const onLogoPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Logo too large (max 4 MB)");
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/projects/${projectId}/report-branding/logo`,
        form, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_uploaded", true);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };
  const removeLogo = async () => {
    setSaving(true);
    try {
      await api.delete(`/projects/${projectId}/report-branding/logo`);
      set("logo_uploaded", false);
      toast.success("Logo removed");
    } catch { toast.error("Could not remove logo"); }
    finally { setSaving(false); }
  };

  const onClientLogoPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Client logo too large (max 4 MB)");
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/projects/${projectId}/report-branding/client-logo`,
        form, { headers: { "Content-Type": "multipart/form-data" } });
      set("client_logo_uploaded", true);
      toast.success("Client logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };
  const removeClientLogo = async () => {
    setSaving(true);
    try {
      await api.delete(`/projects/${projectId}/report-branding/client-logo`);
      set("client_logo_uploaded", false);
      toast.success("Client logo removed");
    } catch { toast.error("Could not remove client logo"); }
    finally { setSaving(false); }
  };

  const heroRef = useRef(null);
  const onHeroPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 16 * 1024 * 1024) {
      toast.error("Image too large (max 16 MB)");
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/projects/${projectId}/report-branding/hero`,
        form, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_hero_image", "custom_upload");
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };

  // ── Compact summary shown when collapsed ───────────────────────────
  const summary = useMemo(() => {
    const rt = REPORT_TYPES.find((x) => x.v === state.report_type)?.l  || state.report_type;
    const st = STANDARDS.find((x) => x.v === state.primary_standard)?.l || state.primary_standard;
    return `${rt} · ${st} · ${state.report_status}`;
  }, [state.report_type, state.primary_standard, state.report_status]);

  // Iter88 · Feb 2026 · Auto-suggested template based on Stage 1's
  // project_stage + commodity. Shown at the top of the templates
  // grid until dismissed or applied.
  const suggested = useMemo(() => {
    const src = {
      project_stage: projectQ.data?.project_stage
                     || cfgQ.data?.project_stage,
      commodity:     projectQ.data?.commodity
                     || cfgQ.data?.commodity,
    };
    if (!src.project_stage && !src.commodity) return null;
    const { key, reason } = suggestTemplate(src);
    const tpl = REPORT_TEMPLATES.find((t) => t.key === key);
    if (!tpl) return null;
    const alreadyMatches =
      state.report_type === tpl.values.report_type
      && state.primary_standard === tpl.values.primary_standard;
    return { tpl, reason, alreadyMatches };
  }, [projectQ.data, cfgQ.data, state.report_type, state.primary_standard]);

  return (
    <div className="rounded-lg border-2 border-slate-200 bg-white shadow-sm mb-4"
         data-testid="cpr-config-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors rounded-t-lg"
        data-testid="cpr-config-toggle">
        <Sliders size={18} weight="fill" className="text-brand-blue" />
        <div className="flex-1 text-left">
          <div className="text-[13px] font-bold text-slate-800">Report Configuration</div>
          <div className="text-[11px] text-slate-500 truncate">{summary}</div>
        </div>
        {saving && <span className="text-[10px] text-slate-500 mr-1">saving…</span>}
        {open ? <CaretDown size={14} className="text-slate-500" />
               : <CaretRight size={14} className="text-slate-500" />}
      </button>

      {open && loaded && (
        <div className="border-t border-slate-200 px-4 py-4 space-y-5">
          {/* ── AI Report Templates ─────────────────────────────────── */}
          <Section title="AI Report Templates">
            <div className="text-[11px] text-slate-500 mb-3">
              One-click starter — pre-fills report type, standard, cover
              titles, and enables AI narratives. You can still tweak
              everything below afterwards.
            </div>
            {suggested && !suggested.alreadyMatches && !suggestionDismissed && (
              <div className="mb-3 flex items-center gap-3 rounded-md bg-brand-blue/5
                                 border border-brand-blue/40 px-3 py-2"
                        data-testid="report-template-suggestion">
                <Sparkle size={14} weight="fill" className="text-brand-blue shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-slate-900">
                    Suggested: {suggested.tpl.name}
                  </div>
                  <div className="text-[10.5px] text-slate-500">{suggested.reason}</div>
                </div>
                <Button size="sm"
                          onClick={() => applyTemplate(suggested.tpl)}
                          data-testid="report-template-suggestion-apply"
                          className="bg-brand-blue hover:bg-brand-blueDeep text-white h-7 text-[11px]">
                  Apply
                </Button>
                <button type="button"
                             onClick={() => setSuggestionDismissed(true)}
                             data-testid="report-template-suggestion-dismiss"
                             className="text-[10.5px] text-slate-500 hover:text-slate-800 underline underline-offset-2">
                  Dismiss
                </button>
              </div>
            )}
            {suggested?.alreadyMatches && (
              <div className="mb-3 flex items-center gap-2 rounded-md bg-emerald-50
                                 border border-emerald-200 px-3 py-1.5"
                        data-testid="report-template-suggestion-matched">
                <CheckCircle size={13} weight="fill" className="text-emerald-600" />
                <div className="text-[11px] text-emerald-900">
                  Great choice — <span className="font-semibold">{suggested.tpl.name}</span> matches the recommendation for this project ({suggested.reason}).
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
                     data-testid="report-templates-grid">
              {REPORT_TEMPLATES.map((tpl) => {
                const active =
                  state.report_type === tpl.values.report_type
                  && state.primary_standard === tpl.values.primary_standard;
                return (
                  <button
                    key={tpl.key} type="button"
                    onClick={() => applyTemplate(tpl)}
                    data-testid={`report-template-${tpl.key}`}
                    className={`text-left rounded-md border p-3 transition-all
                      ${active
                        ? "border-brand-blue bg-brand-blue/5 ring-1 ring-brand-blue/30"
                        : "border-slate-200 bg-white hover:border-brand-blue/60 hover:bg-slate-50"}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[16px] leading-none pt-0.5">{tpl.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] font-bold text-slate-900 truncate">{tpl.name}</span>
                          {active && (
                            <span className="text-[9px] uppercase tracking-widest text-brand-blue font-bold"
                                     data-testid={`report-template-${tpl.key}-active`}>Applied</span>
                          )}
                        </div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">{tpl.tagline}</div>
                        <div className="text-[10px] text-slate-400 mt-1.5 line-clamp-2">
                          {tpl.chapters.slice(0, 4).join(" · ")}
                          {tpl.chapters.length > 4 ? ` · +${tpl.chapters.length - 4} more` : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ── Report Type + Standard + Status ────────────────────── */}
          <Section title="Report Type & Standard">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Report Type">
                <Select value={state.report_type}
                        onChange={(v) => set("report_type", v)}
                        options={REPORT_TYPES}
                        testid="cfg-report-type" />
              </Field>
              <Field label="Primary Standard">
                <Select value={state.primary_standard}
                        onChange={(v) => set("primary_standard", v)}
                        options={STANDARDS}
                        testid="cfg-primary-standard" />
              </Field>
              <Field label="Report Status">
                <Select value={state.report_status}
                        onChange={(v) => set("report_status", v)}
                        options={STATUSES}
                        testid="cfg-report-status" />
              </Field>
            </div>
            <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-700">
              <input type="checkbox" checked={state.llm_chapters}
                     onChange={(e) => set("llm_chapters", e.target.checked)}
                     data-testid="cfg-llm-chapters"
                     className="rounded border-slate-300" />
              Use LLM-generated technical narratives for all chapters
              <span className="text-[10px] text-slate-500 italic">
                (adds up to 30 s; caches for next run)
              </span>
            </label>
          </Section>

          {/* ── Cover Page ─────────────────────────────────────────── */}
          <Section title="Cover Page">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Cover Title (override)">
                <Text value={state.cover_title_override}
                      onChange={(v) => setBranding("cover_title_override", v)}
                      placeholder="e.g. MINERAL RESOURCE ESTIMATE"
                      testid="cfg-cover-title" />
              </Field>
              <Field label="Cover Subtitle (override)">
                <Text value={state.cover_subtitle_override}
                      onChange={(v) => setBranding("cover_subtitle_override", v)}
                      placeholder="e.g. Mineral Resource Estimation"
                      testid="cfg-cover-subtitle" />
              </Field>
              <Field label="Cover Status Label">
                <Text value={state.cover_status_label}
                      onChange={(v) => setBranding("cover_status_label", v)}
                      placeholder="Defaults to DRAFT / REVIEW / FINAL"
                      testid="cfg-cover-status-label" />
              </Field>
              <Field label="Cover Hero Image">
                <Select value={state.cover_hero_image || "off"}
                        onChange={(v) => setBranding("cover_hero_image", v)}
                        options={HERO_MODES}
                        testid="cfg-hero-mode" />
              </Field>
            </div>
            {state.cover_hero_image === "custom_upload" && (
              <div className="mt-2 flex items-center gap-2">
                <input ref={heroRef} type="file"
                       accept="image/png,image/jpeg,image/webp"
                       onChange={onHeroPick} className="hidden"
                       data-testid="cfg-hero-file" />
                <Button variant="outline" size="sm"
                        onClick={() => heroRef.current?.click()}
                        className="h-8 text-[11px]"
                        data-testid="cfg-hero-upload-btn">
                  <ImageIcon size={13} className="mr-1" /> Upload cover image
                </Button>
                <span className="text-[10.5px] text-slate-500">PNG · JPG · WEBP · max 16 MB</span>
              </div>
            )}
          </Section>

          {/* ── Consultant Identity + Logo ────────────────────────── */}
          <Section title="Consultant Identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Consultant Name">
                <Text value={state.consultant_name}
                      onChange={(v) => setBranding("consultant_name", v)}
                      placeholder="e.g. AcmeGeo Consulting Ltd"
                      testid="cfg-consultant-name" />
              </Field>
              <Field label="Consultant Short (top-right chip)">
                <Text value={state.consultant_short}
                      onChange={(v) => setBranding("consultant_short", v)}
                      placeholder="e.g. ACMEGEO"
                      testid="cfg-consultant-short" />
              </Field>
              <Field label="Consultant Address" wide>
                <Text value={state.consultant_address}
                      onChange={(v) => setBranding("consultant_address", v)}
                      placeholder="Registered address · email · phone"
                      testid="cfg-consultant-address" />
              </Field>
              <Field label="Logo Position">
                <Select value={state.logo_position || "top_right"}
                        onChange={(v) => setBranding("logo_position", v)}
                        options={LOGO_POSITIONS}
                        testid="cfg-logo-position" />
              </Field>
              <Field label="Consultant Logo">
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file"
                         accept="image/png,image/jpeg,image/webp,image/svg+xml"
                         onChange={onLogoPick} className="hidden"
                         data-testid="cfg-logo-file" />
                  {state.logo_uploaded ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                            data-testid="cfg-logo-status-uploaded">
                        <CheckCircle size={12} weight="fill" /> Uploaded
                      </span>
                      <Button variant="outline" size="sm" onClick={removeLogo}
                              className="h-7 text-[10px]"
                              data-testid="cfg-logo-remove">
                        <Trash size={11} className="mr-1" /> Remove
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm"
                            onClick={() => fileRef.current?.click()}
                            className="h-7 text-[10px]"
                            data-testid="cfg-logo-upload">
                      <Upload size={11} className="mr-1" /> Upload logo
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Client Logo">
                <div className="flex items-center gap-2">
                  <input ref={clientLogoRef} type="file"
                         accept="image/png,image/jpeg,image/webp,image/svg+xml"
                         onChange={onClientLogoPick} className="hidden"
                         data-testid="cfg-client-logo-file" />
                  {state.client_logo_uploaded ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                            data-testid="cfg-client-logo-status-uploaded">
                        <CheckCircle size={12} weight="fill" /> Uploaded
                      </span>
                      <Button variant="outline" size="sm" onClick={removeClientLogo}
                              className="h-7 text-[10px]"
                              data-testid="cfg-client-logo-remove">
                        <Trash size={11} className="mr-1" /> Remove
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm"
                            onClick={() => clientLogoRef.current?.click()}
                            className="h-7 text-[10px]"
                            data-testid="cfg-client-logo-upload">
                      <Upload size={11} className="mr-1" /> Upload client logo
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Confidentiality Banner (body pages)" wide>
                <Text value={state.confidentiality_notice}
                      onChange={(v) => setBranding("confidentiality_notice", v)}
                      placeholder="e.g. CONFIDENTIAL — RESTRICTED DISTRIBUTION"
                      testid="cfg-confidentiality-notice" />
              </Field>
            </div>
          </Section>

          {/* ── Header ────────────────────────────────────────────── */}
          <Section title="Body-Page Header">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Header — Left (override)">
                <Text value={state.header_left_override}
                      onChange={(v) => setBranding("header_left_override", v)}
                      placeholder="Default: Report title · Project name"
                      testid="cfg-header-left" />
              </Field>
              <Field label="Header — Right (override)">
                <Text value={state.header_right_override}
                      onChange={(v) => setBranding("header_right_override", v)}
                      placeholder="Default: report status"
                      testid="cfg-header-right" />
              </Field>
            </div>
            <label className="mt-2 flex items-center gap-2 text-[12px] text-slate-700">
              <input type="checkbox" checked={!!state.header_show_rule}
                     onChange={(e) => setBranding("header_show_rule", e.target.checked)}
                     data-testid="cfg-header-rule"
                     className="rounded border-slate-300" />
              Show thin horizontal rule beneath header
            </label>
          </Section>

          {/* ── Footer ────────────────────────────────────────────── */}
          <Section title="Body-Page Footer">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Footer — Left">
                <Text value={state.footer_left_override}
                      onChange={(v) => setBranding("footer_left_override", v)}
                      placeholder="Default: consultant short"
                      testid="cfg-footer-left" />
              </Field>
              <Field label="Footer — Centre">
                <Text value={state.footer_center_override}
                      onChange={(v) => setBranding("footer_center_override", v)}
                      placeholder="Default: Report No · Month · Reviewer"
                      testid="cfg-footer-center" />
              </Field>
              <Field label="Footer — Right">
                <Text value={state.footer_right_override}
                      onChange={(v) => setBranding("footer_right_override", v)}
                      placeholder="Default: page number"
                      testid="cfg-footer-right" />
              </Field>
            </div>
            <div className="flex items-center gap-6 mt-2">
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!state.footer_show_rule}
                       onChange={(e) => setBranding("footer_show_rule", e.target.checked)}
                       data-testid="cfg-footer-rule"
                       className="rounded border-slate-300" />
                Show rule above footer
              </label>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!state.footer_show_page_numbers}
                       onChange={(e) => setBranding("footer_show_page_numbers", e.target.checked)}
                       data-testid="cfg-footer-pageno"
                       className="rounded border-slate-300" />
                Show page numbers
              </label>
            </div>
          </Section>

          {/* ── Watermark ─────────────────────────────────────────── */}
          <Section title="Watermark">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!state.watermark_enabled}
                       onChange={(e) => setBranding("watermark_enabled", e.target.checked)}
                       data-testid="cfg-watermark-enabled"
                       className="rounded border-slate-300" />
                Show diagonal status watermark
              </label>
              <Field label="Watermark text (override)">
                <Text value={state.watermark_text_override}
                      onChange={(v) => setBranding("watermark_text_override", v)}
                      placeholder="Default: report status (DRAFT / FINAL)"
                      testid="cfg-watermark-text" />
              </Field>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}


// ── Small internal building blocks ───────────────────────────────────
function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="block text-[11px] font-semibold text-slate-600 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Text({ value, onChange, placeholder, testid }) {
  return (
    <input type="text" value={value || ""} placeholder={placeholder}
           onChange={(e) => onChange(e.target.value)}
           data-testid={testid}
           className="w-full h-8 rounded-md border border-slate-300 px-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand-blue/40" />
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-full h-8 rounded-md border border-slate-300 px-2 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40">
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  );
}
